import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Group,
  Modal,
  ScrollArea,
  Text,
  TextInput,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { useChat } from "./ChatContext";

export default function ChatNewModal() {
  const { t } = useTranslation();
  const {
    newOpen,
    setNewOpen,
    contacts,
    loadContacts,
    startDirect,
    startGroup,
  } = useChat();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [groupName, setGroupName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (newOpen) {
      loadContacts();
      setSelected([]);
      setGroupName("");
      setQuery("");
    }
  }, [loadContacts, newOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((row) =>
      `${row.full_name || ""} ${row.email || ""}`.toLowerCase().includes(q)
    );
  }, [contacts, query]);

  function toggle(contact) {
    const key = `${contact.kind}:${contact.id}`;
    setSelected((prev) =>
      prev.some((row) => `${row.kind}:${row.id}` === key)
        ? prev.filter((row) => `${row.kind}:${row.id}` !== key)
        : [...prev, contact]
    );
  }

  function onCreate() {
    if (!selected.length) return;
    setSaving(true);
    const request =
      selected.length === 1
        ? startDirect(selected[0])
        : startGroup(groupName.trim(), selected);
    request.then(() => setNewOpen(false)).finally(() => setSaving(false));
  }

  return (
    <Modal opened={newOpen} onClose={() => setNewOpen(false)} title={t("chat_new")}>
      <TextInput
        mb="sm"
        placeholder={t("chat_search_people")}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
      />
      {selected.length > 1 && (
        <TextInput
          mb="sm"
          label={t("chat_group_name")}
          value={groupName}
          onChange={(event) => setGroupName(event.currentTarget.value)}
        />
      )}
      <ScrollArea h={280}>
        {filtered.length === 0 && (
          <Text size="sm" c="dimmed">
            {t("chat_no_contacts")}
          </Text>
        )}
        {filtered.map((contact) => {
          const key = `${contact.kind}:${contact.id}`;
          const checked = selected.some((row) => `${row.kind}:${row.id}` === key);
          return (
            <Checkbox
              key={key}
              mb="xs"
              checked={checked}
              label={`${contact.full_name || contact.email} (${contact.kind})`}
              onChange={() => toggle(contact)}
            />
          );
        })}
      </ScrollArea>
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={() => setNewOpen(false)}>
          {t("cancel")}
        </Button>
        <Button
          onClick={onCreate}
          loading={saving}
          disabled={
            selected.length === 0 || (selected.length > 1 && !groupName.trim())
          }
        >
          {selected.length > 1 ? t("chat_new_group") : t("chat_new_direct")}
        </Button>
      </Group>
    </Modal>
  );
}
